<script>
import { mapState, mapActions } from "vuex";
import NavigationWrapper from "@/components/NavigationWrapper";
import QrCode from "qrcode.vue";

export default {
  // Included components
  components: {
    NavigationWrapper,
    QrCode,
  },

  data() {
    return {
      // The code that the user types in to confirm two-factor
      code: "",

      // The two-factor URI for provisioning
      provisioning_uri: "",

      // The manual-entry code for two-factor
      provisioning_code: "",
    };
  },

  computed: {
    // Mapped vuex state
    ...mapState(["user"]),
  },

  // When the component is created, get the current user
  async created() {
    await this.getUser();
  },

  methods: {
    // Hide the disable two-factor modal
    hideDisableModal() {
      this.$refs.disableModal.hide();
    },

    // Hide the enable two-factor modal
    hideEnableModal() {
      this.$refs.enableModal.hide();
    },

    // Enable two-factor (i.e. get a provisioning URI), then show
    // the confirmation modal
    async enable() {
      let result = await this.enableTwoFactor();
      if (result != null) {
        this.provisioning_uri = result.provisioning_uri;
        this.provisioning_code = result.secret.match(/.{1,4}/g).join(" ");
      }
      this.$refs.enableModal.show();
    },

    // Confirm the two factor settings by posting the confirmation
    // code to the API
    async confirm() {
      let success = await this.confirmTwoFactor({ code: this.code });

      // If successful, re-fetch the user, clear the code, and hide the modal
      if (success) {
        await this.getUser();
        this.code = "";
        this.hideEnableModal();
      }
    },

    // Disable two factor and hide the modal
    async disable() {
      await this.disableTwoFactor();
      this.hideDisableModal();
      await this.getUser();
    },

    // Mapped vuex actions
    ...mapActions([
      "enableTwoFactor",
      "confirmTwoFactor",
      "getUser",
      "disableTwoFactor",
    ]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/account">Account</b-breadcrumb-item>
      <b-breadcrumb-item active>Security</b-breadcrumb-item>
    </template>
    <h1 class="mb-3">Security</h1>
    <b-card
      class="text-center py-5"
      v-if="user.two_factor_enabled"
      key="enabled"
    >
      <b-card-title>Two-factor authentication is enabled</b-card-title>
      <b-card-text>
        Two-factor authentication increases the security of your account
      </b-card-text>
      <b-button v-b-modal.disable-modal variant="danger">Disable</b-button>
    </b-card>
    <b-card class="text-center py-5" v-else key="closed">
      <b-card-title>Enable Two-Factor Authentication</b-card-title>
      <b-card-text>
        Increase the security of your account by enabling two-factor
        authentication
      </b-card-text>
      <b-button variant="success" @click="enable">
        Enable Two-Factor Authentication
      </b-button>
    </b-card>
    <b-modal
      ref="enableModal"
      id="enable-modal"
      title="Set up two-factor authentication"
      button-size="sm"
      hide-header-close
    >
      <p>
        <center class="mb-2">
          <QrCode
            :value="provisioning_uri"
            render-as="svg"
            class="Qr-code"
            size="150"
          />
          <span class="code">{{ provisioning_code }}</span>
        </center>
        <b-form-group
          id="code-group"
          label="Authentication code"
          label-for="code"
        >
          <b-form-input
            required
            id="code"
            v-model="code"
            placeholder="Authentication code"
          ></b-form-input>
          <small class="form-text text-muted">
            Install a soft token authenticator app (e.g. Google Authenticator or
            FreeOTP) and use it to scan this QR code. Then enter the displayed
            code here.
          </small>
        </b-form-group>
      </p>
      <template #modal-footer>
        <div class="float-right">
          <b-button class="mr-3" variant="light" @click="hideEnableModal">
            Cancel
          </b-button>
          <b-button variant="success" @click="confirm">
            Enable Two-Factor
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      ref="disableModal"
      id="disable-modal"
      title="Confirm"
      button-size="sm"
      hide-header-close
    >
      <p>
        Are you sure you want to disable two-factor authentication? Doing so
        will decrease the security of your account.
      </p>
      <template #modal-footer>
        <div class="float-right">
          <b-button class="mr-3" variant="light" @click="hideDisableModal">
            Cancel
          </b-button>
          <b-button variant="danger" @click="disable">Disable</b-button>
        </div>
      </template>
    </b-modal>
  </NavigationWrapper>
</template>

<style scoped>
.Qr-code >>> svg {
  width: 250px !important;
  height: 250px !important;
}

.code {
  font-family: monospace;
  text-align: justify;
  font-size: 1.7em;
}
</style>
